import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  formatMoney,
  walletBalanceMinor,
  walletMovementMoney,
  movementMinorFor,
} from "../money.ts";
import type {
  AccountBalances,
  JournalTransaction,
  ProjectedBalance,
  RiderApi,
  WalletSummary,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  EmptyRow,
  Group,
  InfoNote,
  MarketShell,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Wallet` and `Wallet not authoritative` — **one screen in two states**, and that reading is the
 * most consequential decision in this file.
 *
 * | Drawn                                        | From                                               |
 * | -------------------------------------------- | -------------------------------------------------- |
 * | `KES 1,240.00` / `About KES 1,240`           | FIN-03's `netDebitMinor` and FIN-01's `currency`   |
 * | `balance confirmed 12:40`                    | FIN-03's `authoritativeAsOf`, nothing uncounted    |
 * | `balance being updated`                      | the same, with a FIN-02 posting past `checkpoint`  |
 * | `Last confirmed 12:31`                       | FIN-03's `authoritativeAsOf`                       |
 * | `Available to spend on rides and orders`     | designed copy; FIN-01's `status` gates it          |
 * | the `Recent` rows and their amounts          | FIN-02's `lines`, narrowed to this account         |
 * | `confirmed` / `not counted yet`              | each row's `sequenceNumber` against `checkpoint`   |
 * | `Trip trp_8F2K` / `Top-up · M-Pesa`          | **nothing** joins a posting to a trip or a provider|
 * | `processing` on the refund row               | **nothing** — FIN-02 only records posted entries   |
 * | `Top up`                                     | a published command with **no designed form** — Q-148 |
 *
 * ## Why these are two states and not two screens
 *
 * `SCREEN_SPEC.md` lists them as two designed screens and the measurement grades them separately, so
 * they are two addresses and two rows in `navigation.ts`. But they are drawn by one component,
 * because the design itself says they are one thing in two conditions. Its own note:
 *
 * > Wallet: FIN-01 balance with the FIN-03 authoritative flag. When not authoritative, it reads
 * > "About" with a dashed chip and the uncounted item.
 *
 * So the screen a rider sees is decided by the data, not by the address — which is the same shape
 * `Quote` / `3b Quote expired` and `Cart` / `Cart conflict` already have in this application, and the
 * address is corrected to match through `onScreenChange` exactly as those two do. A rider who opens
 * `?page=wallet` while a payment is still being counted gets the honest screen and the address
 * follows.
 *
 * ## The design's note about FIN-01 is wrong, and this is where that lands
 *
 * **FIN-01 has no balance.** Its contract has said so since it was written — _"Wallet identities and
 * lifecycle only; balances and postings are deliberately absent"_ — and `AGENTS.md` is why twice
 * over: a service never reads another's database, and a balance projection is not the accounting
 * source of truth. The ledger is FIN-02 and the projection is FIN-03.
 *
 * So this screen makes **three reads across three services**, and
 * [ADR 0041](../../../docs/adr/0041-a-riders-own-wallet-and-the-balance-projection-behind-it-are-published-under-v1.md)
 * published two of them:
 *
 *   1. **FIN-01's wallet** under `wallet:self` — the currency the figure is written in, and whether
 *      the wallet can be spent from. Bounded by the record: the row carries `ownerType` and
 *      `ownerId`, so the service compares and refuses a wallet that is not this rider's with the
 *      same 404 an absent one gets.
 *   2. **FIN-03's balance projection** under `balance:projection:read` — the figure, the ledger
 *      sequence it includes up to, and the instant that corresponds to. **Not** bounded by the
 *      record, because the projection row carries no subject; ADR 0041 §4 names that as the largest
 *      residual of the change and Q-145 carries the fix.
 *   3. **FIN-02's journal transactions**, which were already public. This is the one that decides
 *      which of the two screens is drawn.
 *
 * ## The flag is `false` forever, so the judgement is this screen's
 *
 * FIN-03 answers `authoritative: false` unconditionally and cannot do otherwise: it learns of a
 * posting by being handed one, so it has no way to know whether FIN-02 has issued another. A
 * projection that claimed to be caught up would be vouching for a ledger it cannot see.
 *
 * What the screen can do is **ask the ledger**. FIN-02 publishes `sequenceNumber` on every row of its
 * listing and FIN-03 publishes the `checkpoint` its figure includes up to, so a posting whose
 * sequence is greater than the checkpoint is one the projection has not absorbed. If there is one,
 * the balance is approximate and the screen says `About`; if there is none, every posting the ledger
 * has issued is in the figure and the screen says the exact amount and when it was confirmed. That
 * comparison is the whole of the difference between the two designed screens, and it is why
 * `authoritativeAsOf` was worth publishing: without it the screen could say the figure was stale and
 * not say as of when.
 *
 * ## The `Recent` group draws the movements, and the stale screen's sample shows one of them
 *
 * The design's stale screen draws a `Recent` group containing only the uncounted row. That is read as
 * its **sample data** rather than as a filter, because the heading says `Recent` rather than
 * `Not counted` and the authoritative screen's `Recent` contains three absorbed movements under the
 * same heading. So one rule draws both: list the recent movements, and badge each one `confirmed` or
 * `not counted yet` by its own sequence. The design's two screens come out as the two readings of
 * that rule, which is what makes it a rule rather than two hard-coded lists.
 *
 * ## FIN-02's listing answers every posting in the tenant, and this screen narrows it
 *
 * `GET /v1/journal-transactions` offers `sourceType`, `currency`, `limit` and `after` and **no
 * account or customer filter**, so the page that arrives contains other riders' postings. This file
 * narrows by `lines.accountId` against the account it is already drawing.
 *
 * That is a narrowing and not a bound, and it is recorded rather than smoothed over: the data crossed
 * the wire. It is the same finding `Refund.tsx` records about `GET /v1/refunds` one domain over, and
 * it is the second half of Q-145 — until something links a wallet to its ledger account and a service
 * can bound these two reads to a person, both of them answer more than this rider's money.
 *
 * ## `Top up` is published and cannot be pressed
 *
 * `POST /v1/wallets/{walletId}/top-ups` exists under ADR 0041, takes an amount and a currency, and is
 * idempotent. `14 WP13 Money and Account.dc.html` draws the button and **no amount entry anywhere**.
 * `CLAUDE.md` section 1: _"Don't add anything that isn't in the design."_ An amount sheet on a money
 * screen is an unapproved screen on a money path, so the button is drawn as drawn, says what is
 * missing when pressed, and **Q-148** records the question with this as what was done meanwhile.
 */

export type WalletPageId = "wallet" | "wallet-not-authoritative";

export interface WalletProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  /** FIN-01's wallet, from `?wallet=`. Q-145: nothing publishes a rider's own wallet identifier. */
  readonly walletId: string | undefined;
  /** FIN-03's ledger account, from `?account=`. Q-145: nothing links the two. */
  readonly accountId: string | undefined;
  /** Which of the two the address asked for. The data decides which is drawn. */
  readonly asked: WalletPageId;
  readonly onScreenChange: (pageId: string) => void;
  readonly onBack: () => void;
}

/** Why `Top up` cannot be pressed. */
const NO_AMOUNT_FORM =
  "The command exists (POST /v1/wallets/{walletId}/top-ups, ADR 0041) and 14 WP13 Money and " +
  "Account.dc.html draws no amount entry anywhere, so this build has nothing to send. Recorded as " +
  "Q-148 rather than invented.";

/** How many of FIN-02's rows to ask for. The design draws three; a page is asked for and narrowed. */
const LEDGER_PAGE = 50;

export function Wallet(props: WalletProps): ReactNode {
  const { api, tenantId, walletId, accountId, asked, onScreenChange } = props;

  const [wallet, setWallet] = useState<WalletSummary | undefined>(undefined);
  const [balances, setBalances] = useState<AccountBalances | undefined>(undefined);
  const [movements, setMovements] = useState<readonly JournalTransaction[]>([]);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  /**
   * All three reads, in one `load`, because the screen is not drawable without all three.
   *
   * The currency the ledger page is asked for comes from the wallet, so the first read gates the
   * third — which is why this is sequential rather than a `Promise.all`. A single `catch` for all
   * three is deliberate: a wallet whose balance could not be read is not a wallet drawn with a blank
   * figure, it is a screen that could not be drawn, and R-23's lesson is that stale or absent money
   * must never be rendered as current.
   */
  const load = useCallback(() => {
    if (walletId === undefined || accountId === undefined) return;
    setState({ kind: "loading" });
    api
      .wallet(walletId)
      .then(async (answer) => {
        const [projection, page] = await Promise.all([
          api.accountBalances({ tenantId, accountId }),
          api.journalTransactions({ currency: answer.currency, limit: LEDGER_PAGE }),
        ]);
        setWallet(answer);
        setBalances(projection);
        setMovements(page);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) =>
        setState(stateForError(error, [SCOPES.wallet, SCOPES.balanceProjection, SCOPES.ledger])),
      );
  }, [api, tenantId, walletId, accountId]);

  useEffect(load, [load]);

  /** This account's own movements, newest first, out of a page that holds the whole tenant's. */
  const mine = useMemo(
    () =>
      accountId === undefined
        ? []
        : movements
            .filter((transaction) => transaction.lines.some((line) => line.accountId === accountId))
            .slice()
            .sort((left, right) => comparePostings(right, left)),
    [movements, accountId],
  );

  const projected = useMemo(
    () => balanceIn(balances, wallet?.currency),
    [balances, wallet?.currency],
  );

  /**
   * The movements the projection has not absorbed: sequence strictly greater than its checkpoint.
   *
   * `BigInt` rather than `Number`, because `sequenceNumber` is published as a decimal **string** on
   * both services and a ledger that outlives 2^53 postings would start comparing rounded values.
   * That is a long way off and it costs one constructor call to never be wrong about it.
   */
  const uncounted = useMemo(() => {
    if (projected === undefined) return new Set<string>();
    const checkpoint = BigInt(projected.checkpoint);
    return new Set(
      mine
        .filter((transaction) => BigInt(transaction.sequenceNumber) > checkpoint)
        .map((transaction) => transaction.id),
    );
  }, [mine, projected]);

  /* The address follows the data, in both directions. See this file's header. */
  const shouldBe: WalletPageId = uncounted.size === 0 ? "wallet" : "wallet-not-authoritative";
  useEffect(() => {
    if (state.kind === "ready" && asked !== shouldBe) onScreenChange(shouldBe);
  }, [state.kind, asked, shouldBe, onScreenChange]);

  if (walletId === undefined || accountId === undefined) {
    return (
      <MarketState
        state={{
          kind: "error",
          code: "no_wallet_in_address",
          message:
            "This screen is about one wallet and one ledger account, and nothing publishes either " +
            "identifier to a rider: FIN-01 has no listing of a customer's wallets and no service " +
            "joins a wallet to the account FIN-02 posts to. Both are carried in the address as " +
            "?wallet= and ?account= meanwhile, and Q-145 records it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading your wallet."
        barTitle="Wallet"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || wallet === undefined || balances === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your wallet."
        barTitle="Wallet"
        barSub="CityCab"
      />
    );
  }

  const settled = uncounted.size === 0;

  return (
    <MarketShell
      screen={settled ? "wallet" : "wallet-stale"}
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Wallet"
      /*
       * `CityCab` in the design is the tenant's own trading name, and nothing in this application's
       * token carries one: the claims are `sub`, `name`, `given_name`, `scope` and `tenant_id`, and
       * TEN-05's branding is read by the consoles rather than published to a handset. Drawn as the
       * absence, which is the same treatment `Refund.tsx` gives its app bar's second line.
       */
      barSub={<Absent words="No tenant trading name is published to this application" />}
      right={{
        glyph: "help",
        label: "Help",
        unavailable: "No help content is published for this screen",
      }}
      badge={chipFor(settled, projected)}
      title={titleFor(settled, projected, wallet)}
      {...(projected === undefined
        ? { titleAbsent: `No ${wallet.currency} balance is projected for this account yet` }
        : {})}
      sub={subFor(settled, projected, wallet)}
      actions={[{ label: "Top up", glyph: "add", primary: true, unavailable: NO_AMOUNT_FORM }]}
    >
      <Group
        heading="Recent"
        rows={mine.map((transaction) => rowFor(transaction, accountId, uncounted))}
        empty={
          <EmptyRow words="No posting in this ledger page touches this account. GET /v1/journal-transactions has no account filter, so this page is the tenant's and this screen narrows it (Q-145)" />
        }
      />

      {/*
       * The design's note, on the stale screen only.
       *
       * `walletstale` carries a `note` and `wallet` does not, so the element is absent rather than
       * empty on the authoritative screen — which matters, because the tray is a flex column with a
       * 12px gap and an empty card would still take its own height.
       */}
      {settled ? null : (
        <InfoNote text="We won't show a balance as exact until our ledger confirms it (FIN-03 authoritative flag). Spending is still possible." />
      )}
    </MarketShell>
  );
}

/** The wallet's own currency out of the account's balances, or nothing projected in it yet. */
function balanceIn(
  balances: AccountBalances | undefined,
  currency: string | undefined,
): ProjectedBalance | undefined {
  if (balances === undefined || currency === undefined) return undefined;
  return balances.balances.find((balance) => balance.currency === currency);
}

/**
 * `balance confirmed 12:40` or `balance being updated`, with the design's own two badge kinds.
 *
 * `done` and `unknown` are the design's: `chip:['done','balance confirmed 12:40']` and
 * `chip:['unknown','balance being updated']`. `unknown` is the dashed pill carrying a literal `?`,
 * which design rule 3.2 reserves for a state nobody has confirmed — which is exactly what an
 * unabsorbed posting makes of a balance.
 *
 * A projection with nothing in this currency yet takes `unknown` too, and the word is this build's:
 * the design draws no chip for a wallet that has never been posted to.
 */
function chipFor(settled: boolean, projected: ProjectedBalance | undefined): BadgeValue {
  if (projected === undefined) return { kind: "unknown", label: "no balance yet" };
  return settled
    ? { kind: "done", label: `balance confirmed ${clock(projected.authoritativeAsOf)}` }
    : { kind: "unknown", label: "balance being updated" };
}

/**
 * `KES 1,240.00` or `About KES 1,240.00`.
 *
 * **The design writes the stale figure without decimals and this draws them, and the difference is
 * deliberate rather than an oversight.** `14 WP13` writes `title:'KES 1,240.00'` on one screen and
 * `title:'About KES 1,240'` on the other, which reads as the design dropping precision where the
 * figure is not exact — a sharper reason than the shorthand `10 Rider App.dc.html` uses. `CLAUDE.md`
 * section 2 asks for full minor units regardless, and it is a platform rule about money against a
 * sample value, so the rule wins here as `money.ts` records it winning on the fare screens. It is the
 * one measured pixel difference on this screen and it is reported as a number rather than closed by
 * dropping the decimals.
 *
 * `About` is the design's own word and carries the whole meaning of the second screen, so it is
 * written exactly and not softened into "approximately" or a tilde.
 */
function titleFor(
  settled: boolean,
  projected: ProjectedBalance | undefined,
  wallet: WalletSummary,
): string {
  if (projected === undefined) return formatMoney(0, wallet.currency);
  const written = formatMoney(walletBalanceMinor(projected.netDebitMinor), wallet.currency);
  return settled ? written : `About ${written}`;
}

/**
 * `Available to spend on rides and orders` or `Last confirmed 12:31. A recent payment is still being
 * counted.`
 *
 * Both are the design's copy word for word, and the second's time is the only part that is data. The
 * sentence is drawn as written whatever the count of uncounted postings: pluralising it into "recent
 * payments are still being counted" would be this build writing copy for a money screen, which is
 * the thing it is least entitled to do. The badge and the rows below already say how many.
 *
 * A wallet that is not `ACTIVE` takes a third line, and it is this build's: the design draws no
 * `FROZEN` wallet. It is flagged on screen through `MarketShell`'s `flag`, which `Wallet` passes
 * nothing to today — the status is drawn in the sub-line instead, because a frozen wallet's whole
 * point is that the sub-line's promise is false.
 */
function subFor(
  settled: boolean,
  projected: ProjectedBalance | undefined,
  wallet: WalletSummary,
): ReactNode {
  if (wallet.status !== "ACTIVE") {
    return `This wallet is ${wallet.status.toLowerCase()} and cannot be spent from.`;
  }
  if (settled) return "Available to spend on rides and orders";
  if (projected === undefined) return "Nothing has been posted to this wallet yet.";
  return `Last confirmed ${clock(projected.authoritativeAsOf)}. A recent payment is still being counted.`;
}

/**
 * One journal transaction as the design writes a `Recent` row.
 *
 * Three of its five parts are published and two are not, and the two are worth naming because they
 * are the same absence twice:
 *
 *   - **The title.** The design writes `Top-up · M-Pesa`, `Trip trp_8F2K`, `Refund · co_7Q2K` and
 *     `Order co_9A1B` — each naming a **business object** or a provider. FIN-02 publishes
 *     `sourceType` (one of six accounting sources) and `sourceId`, and nothing joins either to a
 *     trip, an order or a mobile-money provider.
 *
 *     `Refund · co_7Q2K` looks like it would come out exactly, and it does not: FIN-02 types
 *     `sourceId` as `z.string().uuid()`, so `co_7Q2K` and `trp_8F2K` are not values that row can
 *     ever hold. The design is writing a human-readable business reference and the ledger stores a
 *     v4 UUID, which makes this one absence rather than four near-misses.
 *
 *     So the row is drawn as `${sourceType} · ${sourceId}` — what the contract publishes, whole —
 *     and the design's own `text-overflow: ellipsis` on `.row__title` truncates it. Drawn rather
 *     than dashed out, because a money row with no name at all is worse than one named the way the
 *     ledger names it, and because that is the call `Refund.tsx` already made for FIN-04's
 *     `reasonCode`. It is the largest measured difference on this screen and is reported as one.
 *   - **`processing` on the refund row.** The design badges one movement `caution · processing`, and
 *     FIN-02 records **posted** transactions only: a posting that exists has happened. A refund in
 *     flight is FIN-04's status and this screen does not read FIN-04. So no row is ever drawn
 *     `processing`, and the badge a row does get is the one thing about it this screen can establish.
 *
 * The glyph follows the design's own three: `undo` for a correction or a refund, `north_east` for
 * money in, `south_west` for money out.
 */
function rowFor(
  transaction: JournalTransaction,
  accountId: string,
  uncounted: ReadonlySet<string>,
): RowValue {
  const amountMinor = movementMinorFor(accountId, transaction.lines);
  const counted = !uncounted.has(transaction.id);
  return {
    id: transaction.id,
    lead:
      transaction.sourceType === "refund" || transaction.sourceType === "reversal"
        ? "undo"
        : amountMinor < 0
          ? "south_west"
          : "north_east",
    title: `${capitalise(transaction.sourceType)} · ${transaction.sourceId}`,
    sub: postedAt(transaction.postedAt),
    badge: counted
      ? { kind: "done", label: "confirmed" }
      : { kind: "unknown", label: "not counted yet" },
    amount: walletMovementMoney(amountMinor, transaction.currency),
  };
}

/**
 * Newest first, by **sequence** and not by time.
 *
 * ADR 0027 made FIN-02's listing page over an immutable total ordering and `sequenceNumber` is it.
 * Sorting by `postedAt` would have been the obvious reading of "newest" and would be wrong in the
 * one case that matters: two postings in the same millisecond have an order in the ledger and none in
 * a timestamp, so a correction could draw above the entry it corrects.
 */
function comparePostings(left: JournalTransaction, right: JournalTransaction): number {
  const a = BigInt(left.sequenceNumber);
  const b = BigInt(right.sequenceNumber);
  return a === b ? 0 : a < b ? -1 : 1;
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** `12:40` — the design's own form for a time on this screen, 24-hour and zero-padded. */
function clock(at: string): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * `Today 09:12`, `Yesterday`, `Mon`, `22 Sep` — the four forms `14 WP13` writes for a posting's time.
 *
 * Read from that file's own samples rather than chosen: the wallet screen writes `Today 09:12` and
 * `Yesterday`, the stale screen writes a bare `12:36`, and `Order history` next door writes `Mon` and
 * `22 Sep`. The bare `12:36` is today's time with the word dropped, which is the one form this
 * function does not reproduce — `Today 12:36` is written instead, because two forms for today on two
 * screens of one file is a sample's inconsistency rather than a rule, and the longer one is the one
 * that cannot be misread as yesterday's.
 *
 * `now` is a parameter so the fidelity check's frozen clock reaches it; the capture pins the clock at
 * the design's own `12:40` and these words are relative to it.
 */
function postedAt(at: string, now: Date = new Date()): string {
  const posted = new Date(at);
  const days = Math.floor((startOfDay(now) - startOfDay(posted)) / 86_400_000);
  if (days <= 0) return `Today ${clock(at)}`;
  if (days === 1) return "Yesterday";
  if (days < 7) return posted.toLocaleDateString([], { weekday: "short" });
  return posted.toLocaleDateString([], { day: "numeric", month: "short" });
}

function startOfDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}
